// Authed home / dashboard. Personalized greeting, a hero with a live
// safety score ring, a three-up stat row (medicines / safety alerts /
// questions), tip-of-the-day, four colored quick action tiles, a
// recent activity timeline, and the Siggy trust strip.

import { Link } from 'react-router-dom';
import { AllergyAlertCard } from '../components/AllergyAlertCard';
import { CaregiverKindFirstRun } from '../components/CaregiverKindFirstRun';
import { HomeTour } from '../components/HomeTour';
import { SafetyRing } from '../components/SafetyRing';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { TextSizeFirstRun } from '../components/TextSizeFirstRun';
import { WhatsNewBanner } from '../components/WhatsNewBanner';
import {
  type ActivityKind,
  type DashboardActivity,
  relativeTime,
  useDashboard,
} from '../hooks/useDashboard';
import { firstNameFromEmail, useMe } from '../hooks/useMe';
import { useSubjectName } from '../hooks/useSubjectName';
import { AppPage } from '../layouts/AppPage';
import { t, useLocale } from '../lib/i18n';

interface Action {
  to: string;
  title: string;
  body: string;
  tone: 'blue' | 'mint' | 'peach' | 'neutral';
  icon: JSX.Element;
}

const EXTRA_ACTIONS: Action[] = [
  {
    to: '/pillbox',
    title: 'Daily pillbox.',
    body: 'Check off each dose.',
    tone: 'mint',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect
          x="3"
          y="6"
          width="18"
          height="12"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M9 6v12M15 6v12" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    to: '/symptoms',
    title: 'How you feel.',
    body: 'Daily check-in.',
    tone: 'peach',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="10" r="1" fill="currentColor" />
        <circle cx="15" cy="10" r="1" fill="currentColor" />
        <path
          d="M8.5 14.5c1 1.5 2.3 2 3.5 2s2.5-0.5 3.5-2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    to: '/care-plan',
    title: 'Your week.',
    body: 'Your week at a glance.',
    tone: 'blue',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect
          x="3"
          y="4"
          width="18"
          height="16"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M7 9h10M7 13h7M7 17h4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    to: '/interactions',
    title: 'Interactions.',
    body: 'How meds connect.',
    tone: 'peach',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="6" cy="6" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="18" cy="6" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="6" cy="18" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="18" cy="18" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M9 6h6M6 9v6M18 9v6M9 18h6" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    to: '/food',
    title: 'Food and meals.',
    body: 'Safe dinner ideas.',
    tone: 'mint',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 10h16M5 10v10h14V10" fill="none" stroke="currentColor" strokeWidth="2" />
        <path
          d="M8 6c0-1 1-2 2-2h4c1 0 2 1 2 2v4H8z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    ),
  },
  {
    to: '/pill-identify',
    title: 'Identify a pill.',
    body: 'Snap a loose pill.',
    tone: 'blue',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect
          x="3"
          y="9"
          width="18"
          height="9"
          rx="4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M12 9v9" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    to: '/refills',
    title: 'Refills',
    body: 'See what is low',
    tone: 'peach',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M21 12a9 9 0 1 1-3-6.7M21 4v5h-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/reminders',
    title: 'Reminders',
    body: 'Email at dose time',
    tone: 'neutral',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M6 8a6 6 0 0 1 12 0v5l2 3H4l2-3V8z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M10 19a2 2 0 0 0 4 0"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    to: '/family',
    title: 'Family',
    body: 'Invite a loved one',
    tone: 'mint',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="9" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="17" cy="9" r="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
        <path
          d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6M14 20c0-2.5 1.8-4.5 4-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  // Round-3 fix #13 (Jennifer): the Videos tile was reading as a
  // "coming soon" placeholder before any video existed. Removed from
  // the Home grid until /api/videos/recent returns at least one row.
  // Re-add the tile when Marlee uploads her first weekly drop. The
  // /videos route stays registered in App.tsx so the page renders for
  // people who deep-link in.
];

const ACTIONS: Action[] = [
  {
    to: '/medications/add-by-photo',
    title: 'Add a medicine.',
    body: 'Snap the label.',
    tone: 'blue',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 7h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="13" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
  {
    to: '/chat',
    title: 'Ask Siggy.',
    body: 'Any medicine question.',
    tone: 'mint',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    to: '/medications',
    title: 'My medicines.',
    body: 'Full list and flags.',
    tone: 'peach',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect
          x="3"
          y="8"
          width="18"
          height="13"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M12 12v5M9.5 14.5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    to: '/account',
    title: 'Account.',
    body: 'Settings and privacy.',
    tone: 'neutral',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <path
          d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

function greeting(locale: 'en' | 'es' = 'en'): string {
  const h = new Date().getHours();
  if (h < 5) return t('home.greeting.evening', locale);
  if (h < 12) return t('home.greeting.morning', locale);
  if (h < 17) return t('home.greeting.afternoon', locale);
  return t('home.greeting.evening', locale);
}

function heroCopy(d: { medicationCount: number; flagTotal: number } | null): {
  title: string;
  body: string;
} {
  if (!d || d.medicationCount === 0) {
    return {
      title: 'Let’s set up your list.',
      body: 'Add the medicines and supplements you take. The more we know, the more Siggy can help.',
    };
  }
  if (d.flagTotal === 0) {
    return {
      title: 'You’re all caught up.',
      body: `Your ${d.medicationCount} medicine${d.medicationCount === 1 ? '' : 's'} look good against our safety guidelines for older adults.`,
    };
  }
  return {
    title: 'A few things to review.',
    body: `Siggy found ${d.flagTotal} safety alert${d.flagTotal === 1 ? '' : 's'} across your ${d.medicationCount} medicine${d.medicationCount === 1 ? '' : 's'}. Tap "My medicines" to see them.`,
  };
}

export function Home(): JSX.Element {
  const { me } = useMe();
  const { dashboard } = useDashboard();
  const { locale } = useLocale();
  const subject = useSubjectName();
  const name = me?.email ? firstNameFromEmail(me.email) : '';
  const copy = heroCopy(dashboard);
  const score = dashboard?.safetyScore ?? 100;
  const hasNoMeds = Boolean(dashboard && dashboard.medicationCount === 0);

  // Caregiver mode reframes the greeting: "Good morning. Looking at
  // Marv's list." Round-3 fix #4: pronouns must flip downstream of the
  // first-run picker, not just on the prompt itself.
  const greetingTitle = subject.name
    ? `${greeting(locale)}. Looking at ${subject.possessive ?? subject.name} list.`
    : name
      ? `${greeting(locale)}, ${name}`
      : greeting(locale);

  return (
    <AppPage title={greetingTitle}>
      <WhatsNewBanner />
      <TextSizeFirstRun />
      <CaregiverKindFirstRun />
      <HomeTour />
      <AllergyAlertCard />
      {hasNoMeds ? (
        <section className="welcome-card" aria-label="Get started">
          <SiggyAvatar size={64} />
          <p className="welcome-card__eyebrow">Welcome to MedReview</p>
          <h3 className="welcome-card__title">Let&apos;s set up your medicine list</h3>
          <p className="welcome-card__body">
            Hi! I&apos;m Siggy, your AI medicine helper. I can add your medicines in a quick chat,
            or you can snap a bottle and I&apos;ll read the label for you.
          </p>
          <p
            className="welcome-card__body"
            style={{
              padding: 12,
              background: 'var(--color-surface-secondary)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 14,
              marginTop: 12,
            }}
          >
            MedReview is made by OTCHealthMart, which also sells over-the-counter products. We do
            not use your medicine list to target product ads.{' '}
            <a href="/legal/privacy" style={{ color: 'var(--color-accent)' }}>
              Read our data policy
            </a>
            .
          </p>
          <div className="welcome-card__actions">
            <Link to="/welcome" className="home-hero__cta">
              Chat with Siggy
            </Link>
            <Link
              to="/medications/add-by-photo"
              className="ghost-cta"
              style={{ textDecoration: 'none' }}
            >
              Snap a bottle
            </Link>
          </div>
        </section>
      ) : (
        <section className="home-hero" aria-labelledby="home-hero-title">
          <div className="home-hero__copy">
            <span className="home-hero__eyebrow">Your safety check</span>
            <h2 id="home-hero-title" className="home-hero__title">
              {copy.title}
            </h2>
            <p className="home-hero__body">{copy.body}</p>
            <Link to="/medications" className="home-hero__cta">
              See my medicines
            </Link>
          </div>
          <div className="home-hero__ring" aria-hidden="true">
            <SafetyRing score={score} size={156} label="safety score" />
          </div>
        </section>
      )}

      {hasNoMeds ? null : (
        <section className="talk-cta" aria-label="Ask Siggy">
          <SiggyAvatar size={48} />
          <p className="talk-cta__lead">
            Question about a medicine? <strong>Siggy, your AI medicine helper, is here.</strong>
          </p>
          <Link to="/chat" className="talk-cta__btn">
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <title>Ask Siggy</title>
              <path
                d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
            Ask Siggy
          </Link>
        </section>
      )}

      {/* Round-3 fix #7: Jennifer asked for annual pricing on Home, not
          three taps deep in Compare plans. Only shows for FREE users. */}
      {me?.tier === 'FREE' ? (
        <section
          aria-label="Pricing"
          style={{
            margin: '0 0 var(--space-md)',
            padding: 'var(--space-md)',
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-secondary)',
          }}
        >
          <p style={{ margin: 0, fontSize: 15, color: 'var(--color-text-primary)' }}>
            <strong>Premium:</strong> $9.99/mo or $99/yr. <strong>Family:</strong> $14.99/mo or
            $149/yr.{' '}
            <Link to="/compare-plans" className="account-link" style={{ fontWeight: 600 }}>
              Compare plans.
            </Link>
          </p>
        </section>
      ) : null}

      {(dashboard?.medicationCount ?? 0) > 0 ? (
        <ul className="stat-row" aria-label="Your snapshot">
          <StatCard
            to="/medications"
            label="Medicines"
            value={dashboard?.medicationCount ?? 0}
            tone="blue"
          />
          <StatCard
            to="/medications"
            label="Safety alerts"
            value={dashboard?.flagTotal ?? 0}
            tone={(dashboard?.flagTotal ?? 0) === 0 ? 'mint' : 'peach'}
          />
          <StatCard
            to="/chat"
            label="Questions asked"
            value={dashboard?.conversationCount ?? 0}
            tone="neutral"
          />
        </ul>
      ) : null}

      <section className="tip-card" aria-labelledby="tip-title">
        <span className="tip-card__avatar">
          <SiggyAvatar size={48} />
        </span>
        <div className="tip-card__body">
          <span className="tip-card__eyebrow">Today&apos;s tip from Siggy</span>
          <h3 id="tip-title" className="tip-card__title">
            Keep one master list of every medicine you take.
          </h3>
          <p className="tip-card__text">
            Include prescriptions, over-the-counter pills, vitamins, and herbal supplements. Bring
            the list to every doctor and pharmacist visit. Most medicine mix-ups happen because
            something on the list was missed.
          </p>
        </div>
      </section>

      <h3 className="home-section-title">Quick actions</h3>
      <ul className="action-grid">
        {ACTIONS.map((a) => (
          <li key={a.to} className="action-grid__item">
            <Link
              to={a.to}
              className={`action-tile action-tile--${a.tone}`}
              data-testid={`action-${a.to.replace(/\W+/g, '-')}`}
            >
              <span className="action-tile__icon">{a.icon}</span>
              <span className="action-tile__title">{a.title}</span>
              <span className="action-tile__body">{a.body}</span>
            </Link>
          </li>
        ))}
      </ul>

      <h3 className="home-section-title">Tools</h3>
      <ul className="action-grid">
        {EXTRA_ACTIONS.map((a) => (
          <li key={a.to} className="action-grid__item">
            <Link
              to={a.to}
              className={`action-tile action-tile--${a.tone}`}
              data-testid={`extra-${a.to.replace(/\W+/g, '-')}`}
            >
              <span className="action-tile__icon">{a.icon}</span>
              <span className="action-tile__title">{a.title}</span>
              <span className="action-tile__body">{a.body}</span>
            </Link>
          </li>
        ))}
      </ul>

      {dashboard && dashboard.activity.length > 0 ? (
        <>
          <h3 className="home-section-title">Recent activity</h3>
          <ol className="timeline">
            {dashboard.activity.map((a, idx) => (
              <li key={`${a.kind}-${a.occurredAt}-${idx}`} className="timeline__item">
                <ActivityRow activity={a} />
              </li>
            ))}
          </ol>
        </>
      ) : null}

      <section className="trust-strip" aria-label="About MedReview">
        <SiggyAvatar size={56} />
        <div>
          <p className="trust-strip__title">Reviewed by Dr. Marlee Grounds</p>
          <p className="trust-strip__sub">
            PharmD, BCGP, Board Certified Geriatric Pharmacist. Chief Medical Officer at OTCHealth
            Inc.
          </p>
        </div>
      </section>
    </AppPage>
  );
}

function StatCard({
  to,
  label,
  value,
  tone,
}: {
  to: string;
  label: string;
  value: number;
  tone: 'blue' | 'mint' | 'peach' | 'neutral';
}): JSX.Element {
  return (
    <li className="stat-card-wrap">
      <Link to={to} className={`stat-card stat-card--${tone}`}>
        <span className="stat-card__value">{value}</span>
        <span className="stat-card__label">{label}</span>
      </Link>
    </li>
  );
}

function ActivityRow({ activity }: { activity: DashboardActivity }): JSX.Element {
  const meta = ACTIVITY_META[activity.kind];
  // For medication_added the detail is the med name; for flag_run it's
  // the flag count. Surface either inline so the row reads as content,
  // not a debug log entry.
  let label = meta.label;
  if (activity.kind === 'medication_added' && activity.detail) {
    label = `Added ${activity.detail}`;
  } else if (activity.kind === 'flag_run' && activity.detail) {
    const n = Number(activity.detail);
    label = `Safety scan: ${n} flag${n === 1 ? '' : 's'}`;
  }
  return (
    <div className="timeline__row">
      <span className={`timeline__icon timeline__icon--${meta.tone}`}>{meta.icon}</span>
      <div>
        <p className="timeline__title">{label}</p>
        <p className="timeline__time">{relativeTime(activity.occurredAt)}</p>
      </div>
    </div>
  );
}

const ACTIVITY_META: Record<
  ActivityKind,
  { label: string; tone: 'blue' | 'mint' | 'peach' | 'neutral'; icon: JSX.Element }
> = {
  medication_added: {
    label: 'Medicine added',
    tone: 'blue',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  flag_run: {
    label: 'Safety check completed',
    tone: 'mint',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M5 12l4 4 10-10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  chat_started: {
    label: 'You asked Siggy a question',
    tone: 'neutral',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  consult_requested: {
    label: 'You requested a pharmacist consult',
    tone: 'peach',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
};
